import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { graphql, useStaticQuery } from "gatsby";
import classnames from "classnames";
import { Link } from "./link";
import Modal from "./modal";
import sortPosition from "../utility/sort-position";
import formResultMap from "./data/repair-form-status.json";

export const FormResultModal = ({ show, onHide, formResult, labelManagerLabels }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (show && ref.current) {
      ref.current.focus();
    }
  }, [show]);
  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onHide();
      }
    },
    [onHide]
  );

  const formResultData = useMemo(() => {
    if (!formResult?.Status) return null;
    const key = Object.keys(formResultMap.statusValues).find((key) => {
      try {
        return new RegExp(key).test(formResult.Status);
      } catch (err) {
        console.warn("formResultData err: ", err);
        return false;
      }
    });
    return key ? formResultMap.statusValues[key] : null;
  }, [formResult]);

  // const repair = useMemo(() => formResult?.ListOfRepair?.Repair?.sort(
  //   (a, b) => a.ChangedNo < b.ChangedNo ? 1 : -1),
  //   [formResult]
  // );
  return (
    <Modal>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={ref}
        className={`cart-modal${show ? " show" : ""}`}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={show ? 0 : -1}
        role="dialog"
        aria-hidden={!show}
        onKeyDown={onKeyDown}
      >
        <div className="cart-modal__dialog" role="document">
          {formResult && formResultData && (
            <div className="box-assistenza__modal-result">
              <div className="box-assistenza__modal-info">
                {labelManagerLabels.status}: {formResultData.status}
              </div>
              <div className="box-assistenza__modal-info">
                {labelManagerLabels.formModel}: {formResult.ProductModel}
              </div>
              <div className="box-assistenza__modal-text">{formResultData.description}</div>
              <div className="text-center">
                <button className="btn-link mt-3 mt-md-4" onClick={onHide}>
                  {labelManagerLabels.close}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const AssistanceForm = ({ data }) => {
  const { labelManagerLabels } = useStaticQuery(graphql`
    {
      labelManagerLabels: labelManagerLabels {
        close
        formProductLine
        formModel
        anErrorAsOccurred
        status
      }
    }
  `);
  const { formUrlActionFieldSet, labelFacebookFieldSet, labelGoogleFieldSet } = data;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [formResult, setFormResult] = useState();
  const [formResultVisible, setFormResultVisible] = useState(false);
  const closeFormResult = useCallback(() => {
    setFormResultVisible(false);
    setFormResult();
  }, []);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const form = e.target;
      const fields = [...form.elements].filter((item) => item.nodeName === "INPUT");
      const params = new URLSearchParams(fields.map((field) => [field.name, field.value]));
      const url = `${form.action}?${params.toString()}`;
      setFormResult();
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${url}`, { method: "POST" });
        if (!response.ok) {
          throw new Error(`Error ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        if (data.ErrorCode === "0") {
          setFormResult(data.ServiceRequest);
          setFormResultVisible(true);
        } else {
          setError(data.ErrorMessage);
        }
      } catch (err) {
        console.error("err: ", err);
        setError(labelManagerLabels.anErrorAsOccurred || "An error has occurred");
      }
      setLoading(false);
    },
    [labelManagerLabels]
  );

  return (
    <>
      <form
        action={formUrlActionFieldSet.formUrlAction}
        className="d-flex flex-column align-items-center"
        onSubmit={onSubmit}
      >
        {formUrlActionFieldSet.fieldInput.map((field, i) => (
          <input
            name={field}
            className="box-assistenza__form-input"
            key={i}
            required={true}
            placeholder={field ? `${field.fieldPlaceholder} *` : ""}
          />
        ))}
        <div className="d-flex justify-content-between">
          <div>
            {formUrlActionFieldSet.labelLinkFieldSet
              .filter((link) => link.labelLink && link.link)
              .map((link, i) => (
                <a href={link.link} className="d-block my-2" key={i}>
                  {link.labelLink}
                </a>
              ))}
          </div>
          <div className="text-center mt-3">
            {error && !loading && (
              <div className="box-assistenza__error pb-3 text-center">{error}</div>
            )}
            <button className="box-assistenza__link" disabled={loading}>
              <div className="d-flex align-items-center">
                {loading ? <div className="spinner-border mr-2"></div> : null}
                <span> {formUrlActionFieldSet.labelSubmit}</span>
              </div>
            </button>
          </div>
        </div>
        <div className="d-flex mt-3">
          {labelFacebookFieldSet?.labelFacebook && (
            <a
              href={labelFacebookFieldSet.linkFacebook}
              title={labelFacebookFieldSet?.labelFacebook}
              className="box-assistenza__sign mr-2"
            >
              <img src={labelFacebookFieldSet.imageFacebook.node.publicURL} alt="Facebook" />
            </a>
          )}
          {labelGoogleFieldSet?.labelGoogle && (
            <a
              href={labelGoogleFieldSet.linkGoogle}
              title={labelGoogleFieldSet?.labelGoogle}
              className="box-assistenza__sign"
            >
              <img src={labelGoogleFieldSet?.imageGoogle.node.publicURL} alt="Google" />
            </a>
          )}
        </div>
      </form>
      <FormResultModal
        show={formResultVisible}
        onHide={closeFormResult}
        formResult={formResult}
        labelManagerLabels={labelManagerLabels}
      />
    </>
  );
};

const Assistance = ({ data }) => {
  const { numberBoxLine, titleSectionFieldSet } = data.assistanceModule;
  const sections = titleSectionFieldSet.filter((item) => item.titleSection).sort(sortPosition);

  const rows = numberBoxLine.map((item, i) => {
    const size = parseInt(item[0]);
    const start =
      i > 0 ? numberBoxLine.slice(0, i).reduce((sum, el) => sum + parseInt(el[0]), 0) : 0;
    return sections.slice(start, start + size);
  });

  return (
    <section className="section">
      <div className="container">
        {data.assistanceModule.moduleTitle ? (
          <div className="row">
            <div className="col-12">
              <h3 className="font-medium mt-2 mb-5 text-center">
                {data.assistanceModule.moduleTitle}
              </h3>
            </div>
          </div>
        ) : null}
        {rows.map((items, i) => (
          <div className="row" key={i}>
            {items.map((item, j) => (
              <div
                key={j}
                className={classnames("col-12", {
                  "col-md-6": items.length === 2,
                  "col-md-4": items.length === 3,
                })}
              >
                <div
                  className={classnames("box-assistenza d-flex flex-column", {
                    "box-assistenza--full flex-md-row align-items-md-center": items.length === 1,
                    "box-assistenza--1-2": items.length === 2,
                    "box-assistenza--1-3": items.length === 3,
                  })}
                >
                  {item && item.icon && (
                    <img
                      className="box-assistenza__icon"
                      src={item.icon.node.publicURL}
                      alt={item.icon?.value?.description || ""}
                    />
                  )}
                  <div className="box-assistenza__title">{item.titleSection}</div>
                  {item && item.description && (
                    <div
                      className="box-assistenza__text"
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                  )}
                  {item.ctaLabelFieldSet &&
                    item.ctaLabelFieldSet.ctaLabel &&
                    item.ctaLabelFieldSet.ctaLink && (
                      <div className="box-assistenza__btn">
                        <Link
                          to={item.ctaLabelFieldSet.ctaLink}
                          className={`box-assistenza__link button secondary`}
                        >
                          {item.ctaLabelFieldSet.ctaLabel}
                        </Link>
                      </div>
                    )}
                  {item.formUrlActionFieldSet?.formUrlAction &&
                    item.formUrlActionFieldSet?.fieldInputFieldSet && (
                      <AssistanceForm data={item} />
                    )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

export default Assistance;
export const fragment = graphql`
  fragment AssistanceFragment on LiferayAssistance {
    id
    liferayFields {
      siteId
      articleId
    }
    assistanceModule: moduleTitleFieldSet {
      moduleTitle
      numberBoxLine
      titleSectionFieldSet {
        titleSection
        position
        icon {
          node {
            publicURL
          }
          value {
            description
          }
        }
        description
        ctaLabelFieldSet {
          ctaLink
          colorLabel
          ctaLabel
        }
        formUrlActionFieldSet {
          formUrlAction
          formType
          fieldInputFieldSet {
            fieldInput
            fieldPlaceholder
          }
          labelSubmit
          labelLinkFieldSet {
            labelLink
            link
          }
        }
        labelFacebookFieldSet {
          labelFacebook
          linkFacebook
          imageFacebook {
            node {
              publicURL
            }
          }
        }
        labelGoogleFieldSet {
          labelGoogle
          linkGoogle
          imageGoogle {
            node {
              publicURL
            }
          }
        }
      }
    }
    fields {
      hrefLanguages
    }
  }
`;
